/**
 * Données partagées par les écrans du compte connecté : paquets, préférences et cartes du
 * jour de chaque paquet principal. Chaque écran lit ici et demande un rechargement après
 * une modification.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { creerPlanificateur } from '../../fsrs/planificateur';
import { cartesDuJour, dureeEstimeeMinutes, type CartesDuJour } from '../../seance/compteurs';
import { debutJournee } from '../../seance/journee';
import type { Paquet, Preferences } from '../../supabase/conversions';
import type { Depot } from '../../supabase/depot';
import type { Compte } from './authentification';
import type { Services } from './services';

/** Cartes du jour d'un paquet principal, avec la durée estimée de sa séance. */
export interface JourDuPaquet extends CartesDuJour {
  dureeEstimee: number | null;
  /** Nombre de cartes du paquet et de ses sous-paquets, à réviser ou non (0 : paquet vide). */
  nombreCartes: number;
}

export interface Donnees {
  services: Services;
  compte: Compte;
  depot: Depot;
  /** `null` pendant le premier chargement. */
  paquets: Paquet[] | null;
  preferences: Preferences | null;
  /** Par paquet principal ; absent tant qu'il n'est pas calculé. */
  jours: ReadonlyMap<string, JourDuPaquet>;
  rechargerPaquets(): Promise<void>;
  rechargerPreferences(): Promise<void>;
  /** Recalcule les cartes du jour (après une séance, une carte ajoutée…). */
  rechargerJours(): Promise<void>;
  /** Erreur de chargement (connexion perdue…), ou `null`. */
  erreur: string | null;
}

const Contexte = createContext<Donnees | null>(null);

export function useDonnees(): Donnees {
  const donnees = useContext(Contexte);
  if (donnees === null) throw new Error('useDonnees hors de FournisseurDonnees');
  return donnees;
}

const JOURS_POUR_LA_MOYENNE = 30;

/** Calcule les cartes du jour d'un paquet principal. */
export async function chargerJourDuPaquet(
  depot: Depot,
  paquetPrincipalId: string,
  paquets: readonly Paquet[],
  maintenant = new Date(),
): Promise<JourDuPaquet> {
  const reglages = await depot.reglages(paquetPrincipalId);
  const depuis = new Date(
    Math.min(
      debutJournee(maintenant, reglages.heureDebutJournee).getTime(),
      maintenant.getTime() - JOURS_POUR_LA_MOYENNE * 86_400_000,
    ),
  );
  const [cartes, revisions] = await Promise.all([
    depot.cartesDuPaquet(paquetPrincipalId),
    depot.revisionsDuPaquet(paquetPrincipalId, depuis),
  ]);
  const parents = new Map<string, string | null>();
  const ajouter = (id: string, parent: string | null) => {
    parents.set(id, parent);
    for (const enfant of paquets.filter((p) => p.parentId === id)) ajouter(enfant.id, id);
  };
  ajouter(paquetPrincipalId, null);
  const jour = cartesDuJour({
    paquetPrincipalId,
    parents,
    cartes,
    revisions,
    reglages,
    planificateur: creerPlanificateur(reglages),
    maintenant,
  });
  return {
    ...jour,
    nombreCartes: cartes.length,
    dureeEstimee: dureeEstimeeMinutes(
      revisions.map((r) => r.tempsReponseMs),
      jour.total,
    ),
  };
}

export function FournisseurDonnees({
  services,
  compte,
  children,
}: {
  services: Services;
  compte: Compte;
  children: ReactNode;
}) {
  const depot = useMemo(() => services.depot(compte), [services, compte]);
  const [paquets, setPaquets] = useState<Paquet[] | null>(null);
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [jours, setJours] = useState<ReadonlyMap<string, JourDuPaquet>>(new Map());
  const [erreur, setErreur] = useState<string | null>(null);
  const paquetsCourants = useRef<Paquet[]>([]);

  const signaler = useCallback((e: unknown) => {
    setErreur(e instanceof Error ? e.message : String(e));
  }, []);

  const rechargerJours = useCallback(
    async (liste: readonly Paquet[] = paquetsCourants.current) => {
      try {
        const principaux = liste.filter((p) => p.parentId === null);
        const resultats = await Promise.all(
          principaux.map(
            async (p) => [p.id, await chargerJourDuPaquet(depot, p.id, liste)] as const,
          ),
        );
        setJours(new Map(resultats));
        setErreur(null);
      } catch (e) {
        signaler(e);
      }
    },
    [depot, signaler],
  );

  const rechargerPaquets = useCallback(async () => {
    try {
      const liste = await depot.paquets();
      paquetsCourants.current = liste;
      setPaquets(liste);
      setErreur(null);
      await rechargerJours(liste);
    } catch (e) {
      signaler(e);
    }
  }, [depot, rechargerJours, signaler]);

  const rechargerPreferences = useCallback(async () => {
    try {
      setPreferences(await depot.preferences());
    } catch (e) {
      signaler(e);
    }
  }, [depot, signaler]);

  useEffect(() => {
    void rechargerPaquets();
    void rechargerPreferences();
  }, [rechargerPaquets, rechargerPreferences]);

  const valeur = useMemo<Donnees>(
    () => ({
      services,
      compte,
      depot,
      paquets,
      preferences,
      jours,
      rechargerPaquets,
      rechargerPreferences,
      rechargerJours: () => rechargerJours(),
      erreur,
    }),
    [
      services,
      compte,
      depot,
      paquets,
      preferences,
      jours,
      rechargerPaquets,
      rechargerPreferences,
      rechargerJours,
      erreur,
    ],
  );
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}
